/* =========================================================
   Affiche ÉQUITATION « La carrière » — la carrière de dressage et sa reprise.
   API : SNEqCarriere.render(data, opts) -> string SVG
     data : { mode, titre, kicker, club, lieu, date, cavalier, cheval,
              format  : '40' (carrière de club) | '60' (grande carrière)
              reprise, score, classement, juge,
              mode 'reprise' → figure : 'serpentine' | 'volte' | 'diagonale' | 'huit' }
     opts : { palette }

   Les lettres de la carrière sont le repère que tout cavalier connaît par
   cœur. Elles diffèrent selon le format : une carrière de 20 × 40 n'a que
   six lettres sur ses longs côtés, une 20 × 60 en a dix.

   Auto-suffisant (aucun fetch, aucune image) → re-render serveur direct.
   ========================================================= */
(function () {
  const PW = 800, PH = 1066, MX = 64;

  const PALETTES = {
    craie:    { label:'Craie',    bg:'#F7F4ED', ink:'#0B2D5B', muted:'#8B94A3', accent:'#FF6B2C', line:'#E3DDD0', card:'#FFFFFF', sable:'#E4D5B7', deep:'#D8C7A4' },
    sable:    { label:'Sable',    bg:'#EDE3D2', ink:'#2C3A2E', muted:'#8A8676', accent:'#C4622D', line:'#DDD0B9', card:'#F7F1E6', sable:'#DCCFB6', deep:'#CDBE9E' },
    carriere: { label:'Carrière', bg:'#12362B', ink:'#F4F1E8', muted:'#8FB3A5', accent:'#FF6B2C', line:'#1C5544', card:'#164536', sable:'#C9B489', deep:'#0B271F' },
    nuit:     { label:'Nuit',     bg:'#0B2D5B', ink:'#F7F4ED', muted:'#93A1B8', accent:'#FF9E5C', line:'#1E4272', card:'#12355F', sable:'#C9B489', deep:'#082444' },
  };

  const esc = s => String(s == null ? '' : s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
  const up = s => String(s == null ? '' : s).toUpperCase();
  const fit = (t, maxW, maxS, cwf, ls, minS) => { const n = String(t).length || 1; minS = minS || 12;
    for (let s = maxS; s > minS; s -= 1) if (n * (cwf * s + ls) <= maxW) return s; return minS; };
  const T = (x,y,size,fill,txt,o) => { o = o || {};
    return `<text x="${x}" y="${y}" font-family="${o.f||'Sora'}" font-size="${size}" font-weight="${o.w||600}" fill="${fill}"`
      + (o.ls?` letter-spacing="${o.ls}"`:'') + ` text-anchor="${o.a||'middle'}"` + (o.op?` opacity="${o.op}"`:'') + `>${txt}</text>`; };

  function stats(p, items, y, x0) {
    x0 = x0 || 54;
    const it = items.filter(x => String(x[0] || '').trim());
    if (!it.length) return '';
    const w = (PW - 2*x0) / it.length;
    let s = `<line x1="${x0}" y1="${y-34}" x2="${PW-x0}" y2="${y-34}" stroke="${p.line}" stroke-width="1.5"/>`;
    it.forEach((c, i) => {
      const cc = x0 + w*i + w/2;
      if (i) s += `<line x1="${(x0+w*i).toFixed(0)}" y1="${y-14}" x2="${(x0+w*i).toFixed(0)}" y2="${y+34}" stroke="${p.line}" stroke-width="1.5"/>`;
      s += T(cc, y+6, fit(c[0], w-24, 38, 0.56, 0, 17), p.ink, esc(c[0]), { f:'Oswald', w:700 })
        + T(cc, y+30, 12, p.muted, esc(up(c[1])), { w:600, ls:2.5 });
    });
    return s;
  }

  /* Les deux carrières réglementaires. `pos` = position des lettres le long du
     grand côté, en fraction de la longueur, depuis A. */
  const FORMATS = {
    '40': { ratio: 2, droite: ['K','E','H'], gauche: ['F','B','M'], pos: [0.25, 0.50, 0.75], cote: '20 × 40 m' },
    '60': { ratio: 3, droite: ['K','V','E','S','H'], gauche: ['F','P','B','R','M'], pos: [1/6, 2/6, 3/6, 4/6, 5/6], cote: '20 × 60 m' },
  };

  function carriere(cx, cy, larg, p, F) {
    const lng = larg * F.ratio;
    const x = cx - larg/2, y = cy - lng/2;
    let g = `<rect x="${x.toFixed(1)}" y="${y.toFixed(1)}" width="${larg.toFixed(1)}" height="${lng.toFixed(1)}" rx="3" fill="${p.sable}"/>`
      + `<rect x="${x.toFixed(1)}" y="${y.toFixed(1)}" width="${larg.toFixed(1)}" height="${lng.toFixed(1)}" rx="3" fill="none" stroke="${p.ink}" stroke-width="3"/>`;
    F.droite.forEach((L, i) => { g += T(x + larg + 22, y + lng*F.pos[i] + 6, 18, p.ink, L, { f:'Oswald', w:700 }); });
    F.gauche.forEach((L, i) => { g += T(x - 22, y + lng*F.pos[i] + 6, 18, p.ink, L, { f:'Oswald', w:700 }); });
    g += T(cx, y - 16, 20, p.accent, 'A', { f:'Oswald', w:700 })
      + T(cx, y + lng + 32, 20, p.accent, 'C', { f:'Oswald', w:700 })
      + T(cx + 17, cy + 5, 13, p.muted, 'X', { w:600 });
    return { g, x, y, larg, lng, cx };
  }

  /** Le tracé de la figure exécutée, dans les coordonnées de la carrière. */
  function figure(nom, c, p) {
    const { x, y, larg, lng, cx } = c;
    const trait = d => `<path d="${d}" fill="none" stroke="${p.accent}" stroke-width="3.4" stroke-linecap="round" stroke-linejoin="round" stroke-dasharray="9 8"/>`;
    const depart = `<circle cx="${cx}" cy="${y.toFixed(1)}" r="6" fill="${p.accent}"/>`;
    const arrivee = `<circle cx="${cx}" cy="${(y+lng).toFixed(1)}" r="9" fill="${p.accent}"/><circle cx="${cx}" cy="${(y+lng).toFixed(1)}" r="3.5" fill="${p.sable}"/>`;
    let d = '';
    if (nom === 'volte') {
      /* une volte de 20 m centrée sur X, abordée et quittée par la ligne du milieu */
      const r = larg * 0.44;
      d = `M ${cx} ${y.toFixed(1)} L ${cx} ${(y + lng/2 - r).toFixed(1)}`
        + ` a ${r.toFixed(1)} ${r.toFixed(1)} 0 1 1 0 ${(r*2).toFixed(1)}`
        + ` a ${r.toFixed(1)} ${r.toFixed(1)} 0 1 1 0 ${(-r*2).toFixed(1)}`
        + ` M ${cx} ${(y + lng/2 + r).toFixed(1)} L ${cx} ${(y+lng).toFixed(1)}`;
    } else if (nom === 'diagonale') {
      /* changement de main par la diagonale, de coin à coin */
      const m = larg * 0.10;
      d = `M ${cx} ${y.toFixed(1)} L ${(x + larg - m).toFixed(1)} ${(y + lng*0.14).toFixed(1)}`
        + ` L ${(x + m).toFixed(1)} ${(y + lng*0.86).toFixed(1)}`
        + ` L ${(x + larg - m).toFixed(1)} ${(y + lng*0.92).toFixed(1)} L ${cx} ${(y+lng).toFixed(1)}`;
    } else if (nom === 'huit') {
      const r = larg * 0.34;
      d = `M ${cx} ${y.toFixed(1)} L ${cx} ${(y + lng/2 - r*2).toFixed(1)}`
        + ` a ${r.toFixed(1)} ${r.toFixed(1)} 0 1 1 0 ${(r*2).toFixed(1)}`
        + ` a ${r.toFixed(1)} ${r.toFixed(1)} 0 1 0 0 ${(r*2).toFixed(1)}`
        + ` M ${cx} ${(y + lng/2 + r*2).toFixed(1)} L ${cx} ${(y+lng).toFixed(1)}`;
    } else { /* serpentine de trois boucles */
      d = `M ${cx} ${y.toFixed(1)}`;
      for (let i = 0; i < 3; i++) {
        const y0 = y + lng * (0.10 + i * 0.28), y1 = y + lng * (0.10 + (i+1) * 0.28), s = i % 2 ? -1 : 1;
        d += ` C ${(cx + s*larg*0.62).toFixed(1)} ${y0.toFixed(1)} ${(cx + s*larg*0.62).toFixed(1)} ${y1.toFixed(1)} ${cx} ${y1.toFixed(1)}`;
      }
      d += ` L ${cx} ${(y + lng).toFixed(1)}`;
    }
    return trait(d) + depart + arrivee;
  }

  const NOM_FIGURE = { serpentine: 'Serpentine', volte: 'Volte', diagonale: 'Diagonale', huit: 'Huit de chiffre' };

  function render(data, opts) {
    data = data || {}; opts = opts || {};
    const p = PALETTES[opts.palette] || PALETTES.craie;
    const mode = String(data.mode || 'carriere').toLowerCase();
    const cx = PW / 2;

    const F = FORMATS[String(data.format || '40')] || FORMATS['40'];
    const titre = up(data.titre) || (mode === 'reprise' ? 'MA REPRISE' : 'LA CARRIÈRE');
    const ts = fit(titre, 690, 74, 0.54, 2, 34);
    const kicker = up(data.kicker) || 'DRESSAGE';
    const sous = [up(data.reprise), up(data.date)].filter(Boolean).join(' · ');

    /* Une 20 × 60 est une fois et demie plus longue : sa largeur se déduit de
       la hauteur disponible, sinon elle déborde sur le bandeau du bas. */
    const HAUT_MAX = 500, LARG_MAX = 218;
    const larg = Math.min(LARG_MAX, HAUT_MAX / F.ratio);
    const c = carriere(cx, 592, larg, p, F);
    const corps = c.g + (mode === 'reprise' ? figure(String(data.figure || 'serpentine').toLowerCase(), c, p) : '');

    const barre = mode === 'reprise'
      ? stats(p, [[data.score, 'Score'], [data.classement, 'Classement'], [NOM_FIGURE[String(data.figure || 'serpentine').toLowerCase()] || '', 'Figure']], 946)
      : stats(p, [[data.score, 'Score'], [data.reprise, 'Reprise'], [data.classement, 'Classement']], 946);

    const pied = [esc(up(data.cavalier)), esc(up(data.cheval))].filter(Boolean).join(' &amp; ');
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${PW} ${PH}" font-family="'Sora','Inter',sans-serif">
<rect width="${PW}" height="${PH}" fill="${p.bg}"/>
${T(cx, 84, 19, p.accent, esc(kicker), { w:700, ls:5 })}
${T(cx, 168, ts, p.ink, esc(titre), { f:'Oswald', w:700, ls:2 })}
${sous ? T(cx, 204, fit(sous, 660, 17, 0.66, 3.5, 11), p.muted, esc(sous), { w:600, ls:3.5 }) : ''}
${corps}
${barre}
${pied ? T(cx, 1014, fit(pied, 690, 19, 0.60, 1, 12), p.ink, pied, { f:'Oswald', w:700, ls:1 }) : ''}
</svg>`;
  }

  window.SNEqCarriere = { render, PALETTES, FORMATS };
})();
